import React from 'react';
import { Payment } from '../../../services/billingService';
import { ExternalLink, Loader } from 'lucide-react';

interface PaymentTableProps {
  payments: Payment[];
  isLoading: boolean;
  error?: string;
}

const PaymentTable: React.FC<PaymentTableProps> = ({ payments, isLoading, error }) => {
  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'succeeded':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'failed':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount / 100); // Amount is in cents
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const truncateId = (id: string, length = 8) => {
    return id.length > length ? `${id.substring(0, length)}...` : id;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader size={32} className="text-[#23F47C] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (payments.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-400">No payments found</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-700">
            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Payment ID</th>
            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Customer</th>
            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Amount</th>
            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Status</th>
            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Date</th>
            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Invoice</th>
          </tr>
        </thead>
        <tbody>
          {payments.map((payment, index) => (
            <tr
              key={payment.id}
              className={`border-b border-gray-700 hover:bg-gray-800/50 transition-colors ${
                index % 2 === 0 ? 'bg-gray-900/30' : ''
              }`}
            >
              <td className="px-6 py-4 text-sm text-gray-300">
                <span title={payment.id} className="cursor-help">
                  {truncateId(payment.id)}
                </span>
              </td>
              <td className="px-6 py-4 text-sm">
                <div className="text-gray-300">{payment.userName}</div>
                <div className="text-gray-500 text-xs">{payment.userEmail}</div>
              </td>
              <td className="px-6 py-4 text-sm text-gray-300">
                {formatCurrency(payment.amount, payment.currency)}
              </td>
              <td className="px-6 py-4 text-sm">
                <span
                  className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadgeColor(
                    payment.status
                  )}`}
                >
                  {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                </span>
              </td>
              <td className="px-6 py-4 text-sm text-gray-300">{formatDate(payment.createdAt)}</td>
              <td className="px-6 py-4 text-sm">
                {payment.invoiceUrl ? (
                  <a
                    href={payment.invoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#23F47C] hover:text-[#1dd45f] transition-colors"
                  >
                    View <ExternalLink size={16} />
                  </a>
                ) : (
                  <span className="text-gray-500">-</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PaymentTable;
